/**
 * Sección Módulos: organizada por viaje/guía.
 * El usuario ve sus guías; al elegir una, ve los módulos
 * (Calculadora presupuesto, Checklist) y puede elegir estilo/color (guardado por guía).
 */

import React, { useState, useMemo, useEffect } from "react";
import {
	Box,
	Typography,
	Tabs,
	Tab,
	Paper,
	Button,
	Grid,
	Card,
	CardContent,
	Skeleton,
	FormControl,
	Select,
	MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PaletteIcon from "@mui/icons-material/Palette";
import { BudgetCalculator, TravelChecklist } from "../modules";
import { getModuleDataForCity } from "../modules/cityModulesData";
import api from "../../utils/api";
import { trackEvent } from "../../utils/analytics";

const ACCENT = "#6366F1";

const STORAGE_KEY_PREFIX = "modules_estilo_";

const SMOOTH = "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)";

const ESTILO_OPTIONS = [
	{ id: "clasico", label: "Clásico", color: "#B45309" },
	{ id: "moderno", label: "Moderno", color: "#6366F1" },
	{ id: "minimalista", label: "Minimalista", color: "#64748B" },
	{ id: "vibrante", label: "Vibrante", color: "#E11D48" },
	{ id: "oscuro", label: "Oscuro", color: "#A78BFA" },
];

const ESTILO_THEMES = {
	clasico: {
		accentColor: "#B45309",
		accentColorLight: "rgba(180, 83, 9, 0.12)",
		successColor: "#047857",
		shellBg: "linear-gradient(150deg, #FFFBEB 0%, #FEF3C7 100%)",
		shellPaperBg: "#FFFDF7",
		headerColor: "#78350F",
		subtextColor: "#92400E",
		borderColor: "rgba(180, 83, 9, 0.18)",
		tabActiveBg: "rgba(180, 83, 9, 0.06)",
		cardRadius: 20,
		cardShadow: "0 2px 12px rgba(180, 83, 9, 0.08)",
		cardBorder: "2px solid rgba(180, 83, 9, 0.15)",
		itemBorder: "1px solid rgba(180, 83, 9, 0.08)",
		paperRadius: 20,
		paperShadow: "0 4px 20px rgba(180, 83, 9, 0.06)",
	},
	moderno: {
		accentColor: "#6366F1",
		accentColorLight: "rgba(99, 102, 241, 0.1)",
		successColor: "#10B981",
		shellBg: "linear-gradient(150deg, #EEF2FF 0%, #E0E7FF 100%)",
		shellPaperBg: "#FAFAFF",
		headerColor: "#312E81",
		subtextColor: "#4338CA",
		borderColor: "rgba(99, 102, 241, 0.18)",
		tabActiveBg: "rgba(99, 102, 241, 0.06)",
		cardRadius: 16,
		cardShadow: "0 1px 8px rgba(99, 102, 241, 0.06)",
		cardBorder: "2px solid rgba(99, 102, 241, 0.12)",
		itemBorder: "1px solid rgba(99, 102, 241, 0.06)",
		paperRadius: 16,
		paperShadow: "none",
	},
	minimalista: {
		accentColor: "#64748B",
		accentColorLight: "rgba(100, 116, 139, 0.1)",
		successColor: "#475569",
		shellBg: "#F8FAFC",
		shellPaperBg: "#FFFFFF",
		headerColor: "#0F172A",
		subtextColor: "#475569",
		borderColor: "rgba(100, 116, 139, 0.15)",
		tabActiveBg: "transparent",
		cardRadius: 4,
		cardShadow: "none",
		cardBorder: "1px solid #e2e8f0",
		itemBorder: "1px solid #f1f5f9",
		paperRadius: 6,
		paperShadow: "none",
	},
	vibrante: {
		accentColor: "#E11D48",
		accentColorLight: "rgba(225, 29, 72, 0.12)",
		successColor: "#059669",
		shellBg: "linear-gradient(150deg, #FFF1F2 0%, #FFE4E6 100%)",
		shellPaperBg: "#FFFBFC",
		headerColor: "#881337",
		subtextColor: "#BE123C",
		borderColor: "rgba(225, 29, 72, 0.18)",
		tabActiveBg: "rgba(225, 29, 72, 0.06)",
		cardRadius: 24,
		cardShadow: "0 4px 16px rgba(225, 29, 72, 0.1)",
		cardBorder: "2px solid rgba(225, 29, 72, 0.15)",
		itemBorder: "1px solid rgba(225, 29, 72, 0.08)",
		paperRadius: 24,
		paperShadow: "0 8px 30px rgba(225, 29, 72, 0.06)",
	},
	oscuro: {
		accentColor: "#A78BFA",
		accentColorLight: "rgba(167, 139, 250, 0.2)",
		successColor: "#34D399",
		dark: true,
		shellBg: "linear-gradient(150deg, #0F0F23 0%, #1a1a2e 100%)",
		shellPaperBg: "#1E1E38",
		headerColor: "#E2E8F0",
		subtextColor: "#94A3B8",
		borderColor: "rgba(167, 139, 250, 0.18)",
		tabActiveBg: "rgba(167, 139, 250, 0.1)",
		cardRadius: 16,
		cardShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
		cardBorder: "1px solid rgba(167, 139, 250, 0.15)",
		itemBorder: "1px solid rgba(255, 255, 255, 0.06)",
		paperRadius: 16,
		paperShadow: "0 8px 30px rgba(0, 0, 0, 0.4)",
		paperBg: "#1a1a2e",
		textColor: "#e2e8f0",
	},
};

function getStoredEstilo(guideId) {
	if (!guideId || typeof localStorage === "undefined") return "";
	try {
		return localStorage.getItem(STORAGE_KEY_PREFIX + guideId) || "";
	} catch {
		return "";
	}
}

function setStoredEstilo(guideId, value) {
	if (!guideId || typeof localStorage === "undefined") return;
	try {
		if (value) localStorage.setItem(STORAGE_KEY_PREFIX + guideId, value);
		else localStorage.removeItem(STORAGE_KEY_PREFIX + guideId);
	} catch {}
}

const MODULES = [
	{
		id: "budget",
		label: "Calculadora presupuesto",
		Component: BudgetCalculator,
		propName: "cityPrices",
	},
	{
		id: "checklist",
		label: "Checklist de viaje",
		Component: TravelChecklist,
		propName: "checklistData",
	},
];

const ModulesSection = ({
	user,
	guides = [],
	loading = false,
	onLogin,
	onCreateNew,
	onOpenGuide,
}) => {
	const [selectedGuide, setSelectedGuide] = useState(null);
	const [activeTab, setActiveTab] = useState(0);
	const [selectedEstilo, setSelectedEstilo] = useState("");

	const moduleData = useMemo(
		() =>
			selectedGuide
				? getModuleDataForCity(selectedGuide.id, selectedGuide.name)
				: null,
		[selectedGuide]
	);

	useEffect(() => {
		if (!selectedGuide?.id) {
			setSelectedEstilo("");
			return;
		}
		setSelectedEstilo(getStoredEstilo(selectedGuide.id));
	}, [selectedGuide?.id]);

	const handleEstiloChange = (value) => {
		const next = value || "";
		setSelectedEstilo(next);
		if (selectedGuide?.id) setStoredEstilo(selectedGuide.id, next);
	};

	const resolvedData = moduleData;
	const themeEstilo =
		selectedEstilo && ESTILO_THEMES[selectedEstilo]
			? ESTILO_THEMES[selectedEstilo]
			: null;

	const t = themeEstilo || {};
	const accent = t.accentColor || ACCENT;
	const isDark = !!t.dark;
	const headerClr = t.headerColor || "#1a1a2e";
	const subClr = t.subtextColor || "#64748B";
	const brdClr = t.borderColor || "rgba(0,0,0,0.08)";
	const shellBg = t.shellBg || "transparent";
	const shellPaperBg = t.shellPaperBg || "#fff";
	const paperRadius = t.paperRadius || 16;
	const paperShadow = t.paperShadow || "none";
	const hasTheme = !!selectedEstilo;

	const ActiveComponent = MODULES[activeTab].Component;
	const activePropName = MODULES[activeTab].propName;
	const baseData = resolvedData?.[activePropName] ?? {};
	const mergedData = themeEstilo ? { ...baseData, ...themeEstilo } : baseData;
	const activeProps = {
		direction: "next",
		[activePropName]: mergedData,
	};

	if (selectedGuide) {
		activeProps.projectId = selectedGuide.id;
		activeProps.api = api;
	}

	if (!user) {
		return (
			<Box sx={{ width: "100%", py: 4 }}>
				<Typography
					variant="h4"
					sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
				>
					Módulos por viaje
				</Typography>
				<Typography sx={{ color: "#64748B", mb: 3 }}>
					Cada guía que crees puede tener su propio "Antes de viajar",
					calculadora de presupuesto y checklist. Inicia sesión para ver los
					módulos de tus guías.
				</Typography>
				<Paper
					elevation={0}
					sx={{
						p: 4,
						borderRadius: 3,
						border: "1px solid rgba(0,0,0,0.08)",
						textAlign: "center",
					}}
				>
					<ViewModuleIcon sx={{ fontSize: 56, color: ACCENT, mb: 2 }} />
					<Typography sx={{ color: "#64748B", mb: 2 }}>
						Inicia sesión para gestionar los módulos de contenido de cada viaje.
					</Typography>
					<Button
						variant="contained"
						onClick={onLogin}
						sx={{ borderRadius: 2 }}
					>
						Iniciar sesión
					</Button>
				</Paper>
			</Box>
		);
	}

	if (!selectedGuide) {
		return (
			<Box sx={{ width: "100%", py: { xs: 3, sm: 4 }, minHeight: "50vh" }}>
				<Box sx={{ mb: 4 }}>
					<Typography
						variant="h4"
						sx={{ fontWeight: 700, color: "#1a1a2e", mb: 0.5 }}
					>
						Módulos por viaje
					</Typography>
					<Typography sx={{ color: "#64748B", fontSize: "0.938rem" }}>
						Elige una guía para ver o usar sus módulos: Antes de viajar,
						calculadora de presupuesto y checklist. Cada viaje tiene los suyos.
					</Typography>
				</Box>

				{loading ? (
					<Grid container spacing={2}>
						{[1, 2, 3].map((i) => (
							<Grid item xs={12} sm={6} md={4} key={i}>
								<Skeleton
									variant="rounded"
									height={140}
									sx={{ borderRadius: 3 }}
								/>
							</Grid>
						))}
					</Grid>
				) : guides.length === 0 ? (
					<Paper
						elevation={0}
						sx={{
							p: { xs: 3, sm: 5 },
							py: { xs: 4, sm: 6 },
							borderRadius: 3,
							border: "1px solid rgba(0,0,0,0.08)",
							textAlign: "center",
							minHeight: 280,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<ViewModuleIcon sx={{ fontSize: 56, color: ACCENT, mb: 2 }} />
						<Typography sx={{ color: "#64748B", mb: 2, maxWidth: 360 }}>
							Aún no tienes guías. Crea una para añadir módulos de contenido.
						</Typography>
						<Button
							data-nosnippet
							variant="contained"
							onClick={onCreateNew}
							startIcon={<AddIcon />}
							sx={{ borderRadius: 2 }}
						>
							Crear guía
						</Button>
					</Paper>
				) : (
					<Grid container spacing={2}>
						{guides.map((guide) => (
							<Grid item xs={12} sm={6} md={4} key={guide.id}>
								<Card
									elevation={0}
									sx={{
										borderRadius: 3,
										border: "1px solid rgba(0,0,0,0.06)",
										borderLeft: `3px solid ${ACCENT}`,
										cursor: "pointer",
										transition: SMOOTH,
										"&:hover": {
											borderColor: "rgba(99, 102, 241, 0.25)",
											borderLeftColor: ACCENT,
											boxShadow:
												"0 12px 28px rgba(99, 102, 241, 0.13)",
											transform: "translateY(-2px)",
										},
									}}
									onClick={() => { trackEvent("modules_guide_selected", { guide_id: String(guide.id), guide_name: guide.name }); setSelectedGuide(guide); }}
								>
									<CardContent sx={{ py: 2.5, px: 2.5 }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1.5,
											}}
										>
											<Box
												sx={{
													width: 40,
													height: 40,
													borderRadius: 2,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													background:
														"linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.06) 100%)",
													flexShrink: 0,
												}}
											>
												<ViewModuleIcon
													sx={{ color: ACCENT, fontSize: 22 }}
												/>
											</Box>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant="subtitle1"
													sx={{
														fontWeight: 600,
														color: "#1a1a2e",
														lineHeight: 1.3,
													}}
													noWrap
												>
													{guide.name || "Sin nombre"}
												</Typography>
												<Typography
													variant="body2"
													sx={{
														color: "#94A3B8",
														fontSize: "0.8rem",
													}}
												>
													Ver módulos de este viaje
												</Typography>
											</Box>
										</Box>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				)}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: "100%",
				background: shellBg,
				borderRadius: hasTheme ? 4 : 0,
				p: hasTheme ? { xs: 2, sm: 3 } : 0,
				transition: SMOOTH,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					mb: 1.5,
					flexWrap: "wrap",
					rowGap: 1,
				}}
			>
				<Box
					component="button"
					onClick={() => {
						setSelectedGuide(null);
						setActiveTab(0);
					}}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						border: "none",
						background: "none",
						padding: 0,
						cursor: "pointer",
						color: subClr,
						fontSize: "0.875rem",
						"&:hover": { color: accent },
						transition: SMOOTH,
					}}
					aria-label="Volver a la lista de guías"
				>
					<ArrowBackIcon sx={{ fontSize: 18 }} />
					<span>Volver</span>
				</Box>
				<Box
					sx={{
						width: "1px",
						height: 16,
						backgroundColor: brdClr,
						flexShrink: 0,
						transition: SMOOTH,
					}}
				/>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						color: headerClr,
						fontSize: "1.125rem",
						lineHeight: 1.3,
						transition: SMOOTH,
					}}
				>
					{selectedGuide.name || "Mi viaje"}
				</Typography>
				<Box sx={{ flex: 1, minWidth: 0 }} />

				<FormControl
					size="small"
					sx={{
						minWidth: 170,
						"& .MuiOutlinedInput-root": {
							fontSize: "0.8rem",
							borderRadius: 2,
							backgroundColor: isDark
								? "rgba(255,255,255,0.06)"
								: hasTheme
									? shellPaperBg
									: "#fff",
							transition: SMOOTH,
							"& fieldset": {
								borderColor: brdClr,
								transition: SMOOTH,
							},
							"&:hover fieldset": {
								borderColor: accent,
							},
							"&.Mui-focused fieldset": {
								borderColor: accent,
							},
						},
						"& .MuiSelect-select": {
							color: isDark ? "#E2E8F0" : undefined,
						},
					}}
				>
					<Select
						value={selectedEstilo || ""}
						onChange={(e) => handleEstiloChange(e.target.value)}
						displayEmpty
						renderValue={(v) => {
							const opt = ESTILO_OPTIONS.find((o) => o.id === v);
							return v && opt ? (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.75,
									}}
								>
									<Box
										sx={{
											width: 14,
											height: 14,
											borderRadius: "50%",
											background: opt.color,
											border: "2px solid rgba(255,255,255,0.6)",
											boxShadow: `0 0 0 1px ${opt.color}40`,
											flexShrink: 0,
										}}
									/>
									{opt.label}
								</Box>
							) : (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.75,
										color: isDark ? "#94A3B8" : "#94a3b8",
									}}
								>
									<PaletteIcon sx={{ fontSize: 16 }} />
									Estilo
								</Box>
							);
						}}
						MenuProps={{
							PaperProps: {
								sx: {
									borderRadius: 2.5,
									mt: 1,
									boxShadow:
										"0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
								},
							},
						}}
					>
						<MenuItem value="">
							<em>Sin estilo (por defecto)</em>
						</MenuItem>
						{ESTILO_OPTIONS.map((o) => (
							<MenuItem
								key={o.id}
								value={o.id}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									py: 1,
								}}
							>
								<Box
									sx={{
										width: 16,
										height: 16,
										borderRadius: "50%",
										background: o.color,
										border: "2px solid rgba(255,255,255,0.7)",
										boxShadow: `0 0 0 1px ${o.color}30, 0 2px 4px ${o.color}20`,
										flexShrink: 0,
									}}
								/>
								{o.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{onOpenGuide && (
					<Button
						variant="outlined"
						size="small"
						onClick={() => onOpenGuide(selectedGuide.id)}
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontSize: "0.8rem",
							fontWeight: 600,
							py: 0.5,
							px: 1.5,
							borderColor: brdClr,
							color: subClr,
							transition: SMOOTH,
							"&:hover": {
								borderColor: accent,
								color: accent,
								backgroundColor: `${accent}0A`,
							},
						}}
						startIcon={<EditIcon sx={{ fontSize: 16 }} />}
					>
						Abrir en editor
					</Button>
				)}
			</Box>

			<Tabs
				value={activeTab}
				onChange={(_, v) => { const tabs = ["presupuesto", "checklist", "estilo", "antes_de_viajar"]; trackEvent("modules_tab_changed", { tab: tabs[v] || v, guide_id: selectedGuide?.id ? String(selectedGuide.id) : undefined }); setActiveTab(v); }}
				sx={{
					mb: 1.5,
					minHeight: 40,
					backgroundColor: hasTheme ? t.tabActiveBg : "transparent",
					borderRadius: hasTheme ? 2 : 0,
					px: hasTheme ? 0.5 : 0,
					transition: SMOOTH,
					"& .MuiTab-root": {
						fontWeight: 600,
						textTransform: "none",
						fontSize: "0.813rem",
						minHeight: 40,
						py: 0,
						px: 1.5,
						color: subClr,
						transition: "color 0.25s ease",
					},
					"& .Mui-selected": { color: accent },
					"& .MuiTabs-indicator": {
						backgroundColor: accent,
						borderRadius: 1,
						transition: "background-color 0.3s ease",
					},
					"& .MuiTabs-flexContainer": { gap: 0 },
				}}
			>
				{MODULES.map((m, i) => (
					<Tab key={m.id} label={m.label} id={`module-tab-${i}`} />
				))}
			</Tabs>

			<Paper
				elevation={0}
				sx={{
					borderRadius: `${paperRadius}px`,
					overflow: "hidden",
					border: `1px solid ${brdClr}`,
					boxShadow: paperShadow,
					backgroundColor: shellPaperBg,
					transition: SMOOTH,
					...(isDark && {
						color: t.textColor ?? "#e2e8f0",
					}),
				}}
			>
				<ActiveComponent {...activeProps} />
			</Paper>
		</Box>
	);
};

export default ModulesSection;
